// @givmo/board — a person answering their board's self-assessment (CBG-YR4-ASSESS-WEB, frame s2), at
// `/director/self-assessment?org=&assessment=`. The backend's ask and reminder mails link here
// (`BOARD_DIRECTOR_SELF_ASSESSMENT_PATH`); the person's row on "Your boards" opens it too.
//
// ══ AN OPINION, NOT A STATEMENT OF FACT ═════════════════════════════════════════════════════════
//
// The questions as the officer opened them, in their groups, each statement with its source and the
// five answers; a comment per group; the two questions in the person's own words. Sending is an
// ordinary act on the person's sign-in (D3, the contract §4.1): no signing window, no intent sentence,
// no passkey, because these are opinions, not statements of fact — unlike the year's conflict-of-
// interest answers, which are signed. The person may change their answers until the self-assessment
// is closed; sending again replaces them.
//
// ══ PRIVATE TO THE PERSON (D-CBG-28) ════════════════════════════════════════════════════════════
//
// The promise is said first. The page reads the person's OWN answers only (D2's `my_answers`, by the
// seat's custody chain), and unsent answers stay in this tab (`director-drafts.ts`): nothing
// half-answered reaches Givmo, and no officer can see that someone started. Once it is closed the
// page shows the results everyone on the board reads: counts and comments without names.

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, type BoardSelfAssessmentQuestionsForMeOut } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Link, useSearch } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
  wayHome,
} from '../../director/director-board';
import { useDirectorDraft, type DirectorDraftScope } from '../../director/director-drafts';
import { useBoardDirectorHost } from '../../director/director-host';
import { DirectorReadFailed, useDirectorFailureWords } from '../../director/access-refusal';
import { isCommitteeOnly } from '../../director/director-wire';
import { useDirectorReadsMayFire } from '../../director/read-gate';
import { isNotServed } from '../../lib/not-served';
import { longDay } from '../conflicts/coi-decode';
import { AssessmentAnswerForm } from '../evaluations/assessment-answer-form';
import {
  answersIn,
  applyDraftFields,
  decodeQuestionsForMe,
  decodeResults,
  draftFields,
  draftFromMine,
  extractOwnAssessments,
  hasAnything,
  type AnswerDraft,
} from '../evaluations/assessment-decode';
import { AssessmentResults } from '../evaluations/assessment-results';
import { assessmentWords as w } from '../evaluations/assessment-words';
import { DirectorBoardContent } from './director-board-content';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { DirectorShell } from './director-shell';

const LEAD = 'font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-strong';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const BODY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';
/** A link inside a line of facts: the line's own weight, so it reads as part of the sentence. */
const INLINE_LINK =
  'font-sans text-s13 font-w400 tracking-givmo text-brand-600 underline-offset-2 outline-none hover:text-brand-700 hover:underline focus-visible:underline';

/** The person's own reads, rooted per board so a send refreshes them, and their row on "Your boards". */
export function ownAssessmentKeys(orgRef: string | null) {
  const root = ['board-director-self-assessment', orgRef ?? ''] as const;
  return {
    root,
    list: [...root, 'list'] as const,
    questions: (assessmentId: string) => [...root, 'questions', assessmentId] as const,
    results: (assessmentId: string) => [...root, 'results', assessmentId] as const,
  };
}

/** The search value, when it is a non-empty string. */
function textParam(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function DirectorSelfAssessmentPage() {
  const { board } = useDirectorBoardAddress();
  // CBG-YR6-OUTSIDE (review-215's F5): The board's self-assessment is the board's alone; a seat for someone who isn't
  // on the board, here by address, meets the one not-found and nothing of the board's.
  if (isCommitteeOnly(board))
    return <DirectorBoardOnlyPage testId="page-director-self-assessment" />;
  return <DirectorSelfAssessmentPageForTheBoard />;
}

function DirectorSelfAssessmentPageForTheBoard() {
  const search = useSearch();
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours, me } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const requested = textParam(search.assessment);

  // An address with no assessment (an older link, or the board's own page) opens this seat's open
  // one, else its newest (D1 lists only the ones this seat was asked in).
  const listQuery = useQuery({
    queryKey: ownAssessmentKeys(orgRef).list,
    enabled: mayFire && requested === null,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorListOwnBoardSelfAssessments({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractOwnAssessments(data);
    },
  });
  const own = listQuery.data ?? [];
  const assessmentId =
    requested ?? (own.find((row) => row.status === 'open') ?? own[0])?.assessment_id ?? null;

  const questionsQuery = useQuery({
    queryKey: ownAssessmentKeys(orgRef).questions(assessmentId ?? ''),
    enabled: mayFire && assessmentId !== null,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetOwnBoardSelfAssessmentQuestions({
        path: { org_ref: orgRef ?? '', assessment_id: assessmentId ?? '' },
        throwOnError: true,
      });
      return decodeQuestionsForMe(data);
    },
  });
  const questions = questionsQuery.data ?? null;
  const assessment = questions?.assessment ?? null;
  const closed = assessment?.status === 'closed';

  const resultsQuery = useQuery({
    queryKey: ownAssessmentKeys(orgRef).results(assessmentId ?? ''),
    enabled: mayFire && assessmentId !== null && closed,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardSelfAssessmentResultsForDirector({
        path: { org_ref: orgRef ?? '', assessment_id: assessmentId ?? '' },
        throwOnError: true,
      });
      return decodeResults(data);
    },
  });

  const boardSearch = orgRef === null ? undefined : directorBoardSearch(orgRef);
  // CBG-FIX109: for one seat, the way back opens that seat's page and says so (`wayHome`).
  const home = wayHome(me, w.yourBoards);
  const backToBoards = (
    <Link
      to={home.to}
      search={home.search}
      exact
      className={LINK}
      {...testIdProp('director-self-assessment-back')}
    >
      {home.words}
    </Link>
  );
  const ended = (words: string, testId: string) => (
    <Card testId={testId}>
      <CardBody className="flex flex-col items-start gap-12">
        <p className={BODY}>{words}</p>
        {backToBoards}
      </CardBody>
    </Card>
  );

  let body: ReactNode;
  if (!mayFire || (requested === null && listQuery.isPending)) {
    body = (
      <Skeleton className="h-84 w-full rounded-lg" testId="director-self-assessment-loading" />
    );
  } else if (assessmentId === null) {
    // The list's route miss (the self-assessment not served here) is an answer, as the
    // questions read's is below: the page's one line, never a failure with a retry.
    body =
      listQuery.isError && !isNotServed(listQuery.error) ? (
        <DirectorReadFailed
          read={listQuery}
          line={w.directorUnavailable}
          testId="director-self-assessment-error"
          retryTestId="director-self-assessment-retry"
        />
      ) : (
        ended(w.noneForYou, 'director-self-assessment-none')
      );
  } else if (questionsQuery.isPending) {
    body = (
      <Skeleton className="h-84 w-full rounded-lg" testId="director-self-assessment-loading" />
    );
  } else if (questionsQuery.isError && isNotServed(questionsQuery.error)) {
    // Not asked, or not served here: the backend's one not-found, which says nothing of anyone else.
    body = ended(w.noneForYou, 'director-self-assessment-none');
  } else if (questionsQuery.isError || questions === null || assessment === null) {
    body = (
      <DirectorReadFailed
        read={questionsQuery}
        line={w.directorUnavailable}
        testId="director-self-assessment-error"
        retryTestId="director-self-assessment-retry"
      />
    );
  } else if (assessment.status === 'cancelled') {
    body = ended(w.withdrawnServed, 'director-self-assessment-withdrawn');
  } else if (closed) {
    const results = resultsQuery.data ?? null;
    body = resultsQuery.isPending ? (
      <Skeleton className="h-84 w-full rounded-lg" testId="director-self-assessment-loading" />
    ) : results === null ? (
      <DirectorReadFailed
        read={resultsQuery}
        line={w.directorUnavailable}
        testId="director-self-assessment-error"
        retryTestId="director-self-assessment-retry"
      />
    ) : (
      <AssessmentResults
        results={results}
        titled={false}
        testId="director-self-assessment-results"
        recordLink={(children) => (
          <Link
            to={BOARD_DIRECTOR_PAGES.record}
            params={{ recordId: results.record.record_id }}
            search={boardSearch}
            className={INLINE_LINK}
            data-testid="director-self-assessment-record"
          >
            {children}
          </Link>
        )}
      />
    );
  } else if (assessment.answered_on_paper) {
    body = ended(w.paperRecorded, 'director-self-assessment-paper');
  } else if (!assessment.accepts_answers) {
    // Open, not on paper, and not taking this seat's answers: an earlier holder of the seat sent
    // them (D1's `accepts_answers`, contract §12.3). The backend's own sentence for it; this person
    // neither reads nor replaces those answers (C21).
    body = ended(w.earlierHolderServed, 'director-self-assessment-earlier-holder');
  } else {
    body = (
      <Answering
        key={assessment.assessment_id}
        orgRef={orgRef ?? ''}
        seatId={board?.board_member_id ?? null}
        questions={questions}
      />
    );
  }

  // The title waits only while a read is still on its way: once they settle with no self-assessment
  // to name, the page says what it is, never a loading bar left for good (givmo-design §5).
  const reading =
    !mayFire ||
    (requested === null && listQuery.isPending) ||
    (assessmentId !== null && questionsQuery.isPending);
  return (
    <DirectorShell
      title={
        assessment ? (
          w.title(assessment.label)
        ) : reading ? (
          <Skeleton className="h-28 w-[320px] max-w-full" />
        ) : (
          w.directorPageTitle
        )
      }
      description={assessment?.organization_name ?? board?.organization_name ?? undefined}
      identity={directorBoardIdentity(board)}
      testId="page-director-self-assessment"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {body}
      </DirectorBoardContent>
    </DirectorShell>
  );
}

/** The form, open: the promise, the questions, and Send. */
function Answering({
  orgRef,
  seatId,
  questions,
}: {
  orgRef: string;
  seatId: string | null;
  questions: BoardSelfAssessmentQuestionsForMeOut;
}) {
  const operations = useBoardDirectorOperations();
  const queryClient = useQueryClient();
  const { account } = useBoardDirectorHost();
  const assessment = questions.assessment;
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  // What the person last sent, the form's starting point; their changes are kept in this tab.
  const sent = useMemo(() => draftFromMine(questions.my_answers), [questions.my_answers]);
  const [draft, setDraft] = useState<AnswerDraft>(sent);
  const [error, setError] = useState<string | null>(null);

  const scope = useMemo<DirectorDraftScope | null>(
    () =>
      account?.uid !== undefined && seatId !== null
        ? {
            uid: account.uid,
            orgRef,
            seatId,
            surface: 'self-assessment',
            assessmentId: assessment.assessment_id,
          }
        : null,
    [account?.uid, orgRef, seatId, assessment.assessment_id],
  );
  const keeper = useDirectorDraft(scope, draftFields(draft, sent), (kept) =>
    setDraft(applyDraftFields(sent, kept)),
  );

  const send = useMutation({
    mutationFn: async () => {
      const { data } = await operations.directorSaveOwnBoardSelfAssessmentAnswers({
        path: { org_ref: orgRef, assessment_id: assessment.assessment_id },
        body: answersIn(draft, questions),
        throwOnError: true,
      });
      return decodeQuestionsForMe(data);
    },
    onSuccess: async (saved) => {
      keeper.discard();
      if (saved !== null) {
        queryClient.setQueryData(
          ownAssessmentKeys(orgRef).questions(assessment.assessment_id),
          saved,
        );
      }
      toast.success(w.answersSent);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ownAssessmentKeys(orgRef).root }),
        queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] }),
      ]);
      window.scrollTo({ top: 0 });
    },
    onError: (caught) =>
      setError(failureWords(caught, isGivmoError(caught) ? caught.message : w.couldNotSave)),
  });

  return (
    <>
      <Card testId="director-self-assessment-lead">
        <CardBody className="flex flex-col gap-6">
          <p className={LEAD}>{w.directorLead}</p>
          <p className={QUIET}>{w.directorDue(longDay(assessment.due_on))}</p>
          {assessment.answered_at ? (
            <p className={QUIET} data-testid="director-self-assessment-sent">
              {w.sentAnswersOn(longDay(assessment.answered_at))}
            </p>
          ) : null}
        </CardBody>
      </Card>

      <AssessmentAnswerForm
        questions={questions}
        draft={draft}
        onChange={(next) => {
          setError(null);
          setDraft(next);
        }}
        disabled={send.isPending}
        framing="cards"
        testId="director-self-assessment"
      />

      <div className="flex flex-col items-start gap-8">
        <Button
          appearance="saas"
          tone="primary"
          loading={send.isPending}
          onClick={() => {
            setError(null);
            if (!hasAnything(draft)) {
              setError(w.answerAtLeastOne);
              return;
            }
            send.mutate();
          }}
          testId="director-self-assessment-send"
        >
          {w.sendAnswers}
        </Button>
        {error !== null ? (
          <ErrorMessage testId="director-self-assessment-error-line">{error}</ErrorMessage>
        ) : null}
      </div>
    </>
  );
}
